import { Controller } from 'stimulus'

export default class extends Controller {
  static targets = [
    'clientInput',
    'clientId',
    'results',
    'address',
    'itemsContainer',
    'submitButton',
    'underwritingContainer',
    'carrierSelect',
    'step',
    'stepBadge',
    'stepLabel',
    'stepError'
  ]

  static values = {
    searchUrl: String,
    detailsUrl: String,
    underwritingQuestionsUrl: String,
    initialStep: Number
  }

  connect() {
    this.searchTimeout = null
    this.underwritingTimeout = null
    this.selectedClient = null
    this.clientDetailsLoaded = false
    this._onItemChange = (e) => {
      if (e.target.matches('input[name="item_ids[]"]')) {
        this.scheduleUnderwritingRefresh()
      }
    }
    document.addEventListener('click', this.handleDocumentClick.bind(this))

    this.currentStep = this.resolveInitialStep()

    const hasServerRenderedItems =
      this.hasItemsContainerTarget &&
      this.itemsContainerTarget.querySelector('input[name="item_ids[]"]')

    if (this.hasClientIdTarget && this.clientIdTarget.value) {
      if (!hasServerRenderedItems) {
        this.fetchClientDetails(this.clientIdTarget.value)
      } else {
        this.clientDetailsLoaded = true
        this.attachItemCheckboxListeners()
      }
    }

    this.showStep(this.currentStep)
  }

  disconnect() {
    document.removeEventListener('click', this.handleDocumentClick.bind(this))
    if (this.searchTimeout) clearTimeout(this.searchTimeout)
    if (this.underwritingTimeout) clearTimeout(this.underwritingTimeout)
    if (this.hasItemsContainerTarget) {
      this.itemsContainerTarget.removeEventListener('change', this._onItemChange)
    }
  }

  handleDocumentClick(e) {
    if (this.hasResultsTarget && !this.element.contains(e.target)) {
      this.resultsTarget.classList.add('hidden')
    }
  }

  // Stimulus 2: use plain Number values only (no { type, default } — that is Stimulus 3+).
  resolveInitialStep() {
    if (!this.hasInitialStepValue) return 1
    const n = parseInt(this.initialStepValue, 10)
    if (Number.isFinite(n) && n >= 1 && n <= 4) return n
    return 1
  }

  showStep(stepNumber) {
    this.currentStep = stepNumber
    if (this.hasStepTarget) {
      this.stepTargets.forEach((panel) => {
        const n = parseInt(panel.dataset.step, 10)
        panel.classList.toggle('hidden', n !== stepNumber)
      })
    }
    this.updateStepChrome()
    this.clearStepError()
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = stepNumber !== 4
    }
  }

  updateStepChrome() {
    if (this.hasStepBadgeTarget) {
      this.stepBadgeTargets.forEach((badge) => {
        const n = parseInt(badge.dataset.step, 10)
        const active = n === this.currentStep
        const done = n < this.currentStep
        badge.classList.toggle('border-indigo-600', active || done)
        badge.classList.toggle('bg-indigo-600', active || done)
        badge.classList.toggle('text-white', active || done)
        badge.classList.toggle('border-gray-300', !active && !done)
        badge.classList.toggle('bg-white', !active && !done)
        badge.classList.toggle('text-gray-500', !active && !done)
      })
    }
    if (this.hasStepLabelTarget) {
      this.stepLabelTargets.forEach((label) => {
        const n = parseInt(label.dataset.step, 10)
        const active = n === this.currentStep
        label.classList.toggle('text-indigo-600', active)
        label.classList.toggle('font-semibold', active)
        label.classList.toggle('text-gray-500', !active)
      })
    }
  }

  clearStepError() {
    if (this.hasStepErrorTarget) {
      this.stepErrorTarget.classList.add('hidden')
      this.stepErrorTarget.textContent = ''
    }
  }

  showStepError(message) {
    if (!this.hasStepErrorTarget) return
    this.stepErrorTarget.textContent = message
    this.stepErrorTarget.classList.remove('hidden')
  }

  async nextStep() {
    this.clearStepError()

    if (this.currentStep === 1) {
      if (!this.clientIdTarget.value.trim()) {
        this.showStepError('Select a client from the search results.')
        return
      }
      if (this.hasCarrierSelectTarget && !this.carrierSelectTarget.value) {
        this.showStepError('Select a carrier.')
        return
      }
      if (!this.clientDetailsLoaded) {
        this.showStepError('Wait for the client’s address and items to finish loading.')
        return
      }
      const hasItemCheckboxes =
        this.hasItemsContainerTarget &&
        this.itemsContainerTarget.querySelector('input[name="item_ids[]"]')
      if (!hasItemCheckboxes) {
        this.showStepError('This client has no items in their collection, or data failed to load.')
        return
      }
      this.showStep(2)
      return
    }

    if (this.currentStep === 2) {
      if (this.selectedItemIds().length === 0) {
        this.showStepError('Select at least one collection item.')
        return
      }
      await this.refreshUnderwriting()
      this.showStep(3)
      return
    }

    if (this.currentStep === 3) {
      this.showStep(4)
    }
  }

  previousStep() {
    this.clearStepError()
    if (this.currentStep <= 1) return
    this.showStep(this.currentStep - 1)
  }

  search() {
    const q = this.clientInputTarget.value.trim()
    if (this.searchTimeout) clearTimeout(this.searchTimeout)
    if (q.length < 2) {
      this.clearResults()
      return
    }
    this.searchTimeout = setTimeout(() => this.fetchClients(q), 300)
  }

  async fetchClients(q) {
    const path = this.searchUrlValue
    if (!path || typeof path !== 'string') return this.clearResults()
    const url = path.startsWith('http') ? new URL(path) : new URL(path, window.location.origin)
    url.searchParams.set('q', q)
    try {
      const resp = await fetch(url.toString(), { headers: { Accept: 'application/json' } })
      const clients = await resp.json()
      this.renderResults(clients)
    } catch (_e) {
      this.clearResults()
    }
  }

  renderResults(clients) {
    if (!this.hasResultsTarget) return
    if (clients.length === 0) {
      this.resultsTarget.innerHTML = '<div class="px-3 py-2 text-sm text-gray-500">No clients found</div>'
    } else {
      this.resultsTarget.innerHTML = clients.map(c => `
        <button type="button" class="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none" data-action="click->quote-calculator#selectClient" data-client-id="${c.id}" data-client-name="${(c.full_name || '').replace(/"/g, '&quot;')}">
          ${this.escapeHtml(c.full_name || '')} ${c.email ? `<span class="text-gray-500">(${this.escapeHtml(c.email)})</span>` : ''}
        </button>
      `).join('')
    }
    this.resultsTarget.classList.remove('hidden')
  }

  selectClient(e) {
    const id = e.currentTarget.dataset.clientId
    const name = e.currentTarget.dataset.clientName
    this.selectedClient = { id, name }
    if (this.hasClientIdTarget) this.clientIdTarget.value = id
    if (this.hasClientInputTarget) this.clientInputTarget.value = name
    this.clearResults()
    this.clientDetailsLoaded = false
    this.fetchClientDetails(id)
  }

  attachItemCheckboxListeners() {
    if (!this.hasItemsContainerTarget) return
    this.itemsContainerTarget.removeEventListener('change', this._onItemChange)
    this.itemsContainerTarget.addEventListener('change', this._onItemChange)
  }

  scheduleUnderwritingRefresh() {
    if (this.underwritingTimeout) clearTimeout(this.underwritingTimeout)
    this.underwritingTimeout = setTimeout(() => this.refreshUnderwriting(), 300)
  }

  selectedItemIds() {
    if (!this.hasItemsContainerTarget) return []
    return Array.from(this.itemsContainerTarget.querySelectorAll('input[name="item_ids[]"]:checked')).map(el => el.value)
  }

  buildUnderwritingRequestBody() {
    const body = new FormData()
    body.append('client_id', this.clientIdTarget.value)
    this.selectedItemIds().forEach((id) => body.append('item_ids[]', id))

    if (this.hasUnderwritingContainerTarget) {
      const fields = this.underwritingContainerTarget.querySelectorAll('input, select, textarea')
      fields.forEach((el) => {
        const name = el.name
        if (!name || !name.startsWith('quote')) return

        if (el.type === 'checkbox') {
          if (el.checked) body.append(name, el.value || 'true')
        } else if (el.type === 'radio') {
          if (el.checked) body.append(name, el.value)
        } else {
          body.append(name, el.value)
        }
      })
    }
    return body
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
  }

  async refreshUnderwriting() {
    if (!this.hasUnderwritingContainerTarget) return

    const block = this.underwritingContainerTarget.closest('[data-quote-calculator-underwriting-block]')
    const clientId = this.clientIdTarget?.value
    const ids = this.selectedItemIds()

    if (!clientId || ids.length === 0) {
      this.underwritingContainerTarget.innerHTML = '<p class="text-sm text-gray-500">Select at least one collection item to see underwriting questions.</p>'
      block?.classList.toggle('hidden', !clientId)
      return
    }

    const path = this.underwritingQuestionsUrlValue
    if (!path || typeof path !== 'string') {
      this.underwritingContainerTarget.innerHTML = '<p class="text-sm text-red-600">Underwriting questions URL is not configured.</p>'
      block?.classList.remove('hidden')
      return
    }

    const url = path.startsWith('http') ? path : new URL(path, window.location.origin).toString()
    const headers = { Accept: 'text/html' }
    const token = this.csrfToken()
    if (token) headers['X-CSRF-Token'] = token

    try {
      const resp = await fetch(url, {
        method: 'POST',
        body: this.buildUnderwritingRequestBody(),
        headers
      })
      if (!resp.ok) {
        this.underwritingContainerTarget.innerHTML = `<p class="text-sm text-red-600">Could not load underwriting questions (${resp.status}).</p>`
        block?.classList.remove('hidden')
        return
      }
      const html = await resp.text()
      this.underwritingContainerTarget.innerHTML = html
      block?.classList.remove('hidden')
    } catch (_e) {
      this.underwritingContainerTarget.innerHTML = '<p class="text-sm text-red-600">Failed to load underwriting questions.</p>'
      block?.classList.remove('hidden')
    }
  }

  async fetchClientDetails(id) {
    const path = this.detailsUrlValue
    if (!path || typeof path !== 'string') return
    const pathWithId = path.replace('__ID__', encodeURIComponent(id))
    const url = pathWithId.startsWith('http') ? pathWithId : new URL(pathWithId, window.location.origin).toString()
    try {
      const resp = await fetch(url, { headers: { Accept: 'application/json' } })
      const data = await resp.json()
      if (this.hasAddressTarget) {
        this.addressTarget.textContent = data.full_address || 'No address on file'
        this.addressTarget.closest('[data-quote-calculator-address-block]')?.classList.remove('hidden')
      }
      if (this.hasItemsContainerTarget) {
        const items = data.items || []
        if (items.length === 0) {
          this.itemsContainerTarget.innerHTML = '<p class="text-sm text-gray-500">No items in collection</p>'
        } else {
          this.itemsContainerTarget.innerHTML = items.map(item => `
            <label class="flex items-center gap-2 py-1 text-sm">
              <input type="checkbox" name="item_ids[]" value="${this.escapeHtml(item.id)}" class="rounded border-gray-300">
              <span>${this.escapeHtml(item.name || 'Item')} — ${this.escapeHtml(this.stringValue(item.category))} (${this.formatValue(item.value)})</span>
            </label>
          `).join('')
        }
        this.itemsContainerTarget.closest('[data-quote-calculator-items-block]')?.classList.remove('hidden')
      }
      this.clientDetailsLoaded = true
      this.attachItemCheckboxListeners()
      this.refreshUnderwriting()
    } catch (_e) {
      this.clientDetailsLoaded = false
      if (this.hasAddressTarget) this.addressTarget.textContent = 'Failed to load address'
      if (this.hasItemsContainerTarget) this.itemsContainerTarget.innerHTML = '<p class="text-sm text-red-600">Failed to load items</p>'
      if (this.hasUnderwritingContainerTarget) {
        this.underwritingContainerTarget.innerHTML = '<p class="text-sm text-red-600">Failed to load client details.</p>'
      }
    }
  }

  formatValue(val) {
    const num = this.numericValue(val)
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(isNaN(num) ? 0 : num)
  }

  numericValue(val) {
    if (val == null) return NaN
    if (typeof val === 'number' && !isNaN(val)) return val
    if (typeof val === 'string') return parseFloat(val)
    if (typeof val === 'object' && val !== null) {
      const n = val.amount ?? val.value ?? val.total
      return typeof n === 'number' ? n : parseFloat(n)
    }
    return NaN
  }

  stringValue(val) {
    if (val == null) return ''
    if (typeof val === 'string') return val
    if (typeof val === 'object' && val !== null) return String(val.value ?? val.name ?? val)
    return String(val)
  }

  clearResults() {
    if (this.hasResultsTarget) {
      this.resultsTarget.innerHTML = ''
      this.resultsTarget.classList.add('hidden')
    }
  }

  clearSelection() {
    this.selectedClient = null
    this.clientDetailsLoaded = false
    if (this.hasClientIdTarget) this.clientIdTarget.value = ''
    if (this.hasClientInputTarget) this.clientInputTarget.value = ''
    if (this.hasAddressTarget) {
      this.addressTarget.textContent = ''
      this.addressTarget.closest('[data-quote-calculator-address-block]')?.classList.add('hidden')
    }
    if (this.hasItemsContainerTarget) {
      this.itemsContainerTarget.innerHTML = ''
      this.itemsContainerTarget.closest('[data-quote-calculator-items-block]')?.classList.add('hidden')
    }
    if (this.hasUnderwritingContainerTarget) {
      this.underwritingContainerTarget.innerHTML = ''
      this.underwritingContainerTarget.closest('[data-quote-calculator-underwriting-block]')?.classList.add('hidden')
    }
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = true
    this.showStep(1)
  }

  escapeHtml(s) {
    if (s == null) return ''
    const div = document.createElement('div')
    div.textContent = s
    return div.innerHTML
  }
}
